import { AvailabilityErrorType } from 'interfaces'
import { show as showNiceModal } from '@ebay/nice-modal-react'
import { Circle } from '@mui/icons-material'
import { observer } from 'mobx-react-lite'
import { setOrderResponses } from 'orderConfirmation'
import { ReactElement, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Config, IS_INTEGRATED_VERSION } from 'src/config/ConfigManager'
import { getCxmlDetails } from 'src/helpers/cxmlDetailsUtil'
import { StoreInstances } from 'src/store/StoreInstancesContainer'
import { CartMode } from 'src/store/models/ShoppingCartModels'
import { UserAttributeKey } from 'src/store/user/interfaces'
import { breakpoints } from 'src/theme/breakpoints'
import { Button } from 'src/ui-components'
import styled from 'styled-components'
import { embeddedConfigManager } from '../../../../src/api/embedded/EmbeddedConfigManager'
import { EmbeddedCommunicationsManager } from '../../../../src/api/embedded/EmbeddedCommunicationsManager'
import { CartButtonValues } from '../../../../src/api/embedded/types/partsBasketSpec'
import { CartPriceChangedProductsModal } from './CartPriceChangedProductsModal'
import { OrderFormValidationException } from '../exceptions/OrderFormValidationException'
import { timeout } from 'src/theme/timeout'
import { getCartMode } from '../Utils'
import { RootState, store, useAppDispatch } from 'store'
import { cartSlice, setMultiCartValidated } from '../store/cartStore'
import { useSelector } from 'react-redux'
import { CofirmationModal } from 'src/ui-components/modals/ConfirmationModal'
import { validateCartPricesAndQty } from '../store/storeThunks'
import { GaTrackOption } from 'src/config/analytics/tracking/utils'
import ShowNotification from 'src/ui-components/modals/ToastNotifcationNotiStack'
import { ToastNotificationVariant } from 'src/ui-components/util/Interfaces'
import {
  submitTransferPartsForm,
  transferAllCarts,
} from '../utils/transferPartsForm'
import { formatVehicleDisplay } from 'src/helpers/utils'
import { I18n, Translate2 as Translate } from 'common'
import { getInvalidVehicles, trackBeginCheckout } from '../utils/Utils'
import { getVehicleProductsErrors } from '../utils/Utils_testable'
import { orderAllCarts } from '../utils/orderUtils'
import { OrderTimeoutError } from './OrderTimeoutError'
import { useQuoteStore } from 'pages/quotes/src/store/QuoteStore'

export const OrderAllSellerCarts = observer((): ReactElement => {
  const dispatch = useAppDispatch()
  const { getSellerData, isMultiCartValidated } = useSelector(
    (state: RootState) => ({
      getSellerData: state.cart.cartData,
      isMultiCartValidated: state.cart.isMultiCartValidated,
    })
  )

  const navigate = useNavigate()
  const { saveMultiSellerQuote } = useQuoteStore()
  const isCartNormalMode = Config?.isNotCartOnlyMode
  const { isAstPunchOut } = EmbeddedCommunicationsManager
  const [processingTransfer, setProcessingTransfer] = useState<boolean>(false)
  // tracks whether validation found silent errors across all carts
  // used to show the warning message and change button text to 'proceedWithOrder'
  const [hasValidationFailed, setHasValidationFailed] = useState<boolean>(false)
  const isValidatingCartRef = useRef(false)
  const timeoutRetryCountRef = useRef(0)

  const { cxmlData, token } = getCxmlDetails()

  const vehiclesWithParts = useMemo(() => {
    return getSellerData.vehicles.filter((v) =>
      v.sellers.some((s) => s.locations.some((l) => l.parts.length > 0))
    )
  }, [getSellerData.vehicles])

  const orderButtonText =
    vehiclesWithParts.length > 1 ? 'orderAllCarts' : 'order'

  const processingOrder = useSelector(
    (state: RootState) => state.ui.processingOrder
  )

  const isOrderButtonDisplayed = useMemo(() => {
    // enabling order button when there is only single vehicle cart.
    if (
      StoreInstances.userStore.getUserAttribute(
        UserAttributeKey.orderAllCarts
      ) === 'false' &&
      getCartMode() === CartMode.SINGLE_VEHICLE &&
      vehiclesWithParts.length === 1
    ) {
      return true
    }

    // disabling order button, based on orderAllCarts attribute.
    if (
      StoreInstances.userStore.getUserAttribute(
        UserAttributeKey.orderAllCarts
      ) === 'false'
    ) {
      return false
    }
    if (!IS_INTEGRATED_VERSION) return true
    if (isAstPunchOut && isCartNormalMode) return false
    if (
      embeddedConfigManager.cartButtonValue === CartButtonValues.ORDER ||
      embeddedConfigManager.cartButtonValue === CartButtonValues.BOTH
    )
      return true

    return false
  }, [isAstPunchOut, isCartNormalMode, vehiclesWithParts.length])

  const isTransferButtonDisplayed = useMemo(() => {
    return (
      IS_INTEGRATED_VERSION &&
      embeddedConfigManager.cartButtonValue !== CartButtonValues.ORDER
    )
  }, [])

  const handleDismiss = {
    text: Translate('dismiss'),
    action: () => {
      //
    },
  }

  const getMultiVehiclesInfo = (): string[] => {
    const vehiclesData: string[] = []
    vehiclesWithParts?.forEach((data) => {
      if (data.vehicle) {
        vehiclesData.push(formatVehicleDisplay(data.vehicle))
      }
    })
    return vehiclesData
  }

  const handleSaveAllAsQuotes = async () => {
    if (!getSellerData.vehicles) return
    try {
      await Promise.all(
        getSellerData.vehicles.map((sellerCartVehicle) =>
          saveMultiSellerQuote(sellerCartVehicle)
        )
      )
      ShowNotification({
        message: 'allVehiclesWereSavedAsQuotes',
        variant: ToastNotificationVariant.SUCCESS,
        link: {
          text: Translate('viewQuotes'),
          action: () => navigate('/quotes'),
        },
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      ShowNotification({
        message: JSON.stringify(error.message),
        variant: ToastNotificationVariant.ERROR,
      })
    }
  }

  const handleAllSellerCarts = async () => {
    try {
      const response = await orderAllCarts()
      if (!response) return
      if (getCartMode() === CartMode.CART_ONLY) return
      setOrderResponses(response)
      store.dispatch(
        cartSlice.actions.removeAllVehiclesFromCart(GaTrackOption.doNotTrack)
      )
      store.dispatch(cartSlice.actions.setDisplayCartFormErrors(false))
      navigate('/orderConfirmation')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (e instanceof OrderFormValidationException) {
        ShowNotification({
          message: JSON.stringify(e.message),
          variant: ToastNotificationVariant.ERROR,
        })
      } else {
        StoreInstances.uiStore.displayErrorNotification(
          'unableToPlaceOrderPleaseContactTheAdministrator',
          handleDismiss,
          timeout.orderErrorNotification
        )
      }
    } finally {
      isValidatingCartRef.current = false
    }
  }

  const showVehiclesData = (vehiclesData: string[]) => {
    return (
      <MainContainer>
        {vehiclesData?.map((data, index) => {
          return (
            <VehicleContainer
              key={`${data}_${index}`}
              style={{ width: '100%' }}
            >
              <Circle
                sx={{ width: '10px', height: '10px', marginRight: '15px' }}
              />
              <Label>{`${data}`}</Label>
            </VehicleContainer>
          )
        })}
      </MainContainer>
    )
  }

  const handleTransfer = () => {
    trackBeginCheckout()
    EmbeddedCommunicationsManager.transferSellerCart()
  }

  const cancelTransfer = () => {
    EmbeddedCommunicationsManager.cancelCallBack()
  }

  const validateCartAvailabilityErrors = async (): Promise<void> => {
    if (isValidatingCartRef.current) return
    isValidatingCartRef.current = true
    dispatch(cartSlice.actions.setDisplayCartFormErrors(true))
    const invalidVehicles = getInvalidVehicles(getSellerData?.vehicles || [])
    if (invalidVehicles.length) {
      ShowNotification({
        message: 'orderFormIsIncomplete',
        variant: ToastNotificationVariant.ERROR,
      })
      isValidatingCartRef.current = false
      return
    }
    if (isMultiCartValidated) {
      handleAllSellerCarts()
      return
    }

    try {
      const { hasSameCartErrors, vehiclePriceChangeErrors, hasTimeout } =
        await dispatch(validateCartPricesAndQty()).unwrap()

      if (hasTimeout) {
        timeoutRetryCountRef.current += 1
        if (timeoutRetryCountRef.current >= 2) {
          // Second timeout - show timeout modal
          showNiceModal(OrderTimeoutError, {
            onTryAgain: () => {
              timeoutRetryCountRef.current = 0
              dispatch(setMultiCartValidated({ value: false }))
              isValidatingCartRef.current = false
              validateCartAvailabilityErrors()
            },
            onSaveAsQuote: handleSaveAllAsQuotes,
          })
        } else {
          // First timeout - show toast and auto-retry
          ShowNotification({
            message: 'itsTakingLongerThanUsualToPerformYourInquiry',
            variant: ToastNotificationVariant.ERROR,
          })

          isValidatingCartRef.current = false
          validateCartAvailabilityErrors()
        }
        return
      }

      // Reset retry count on success
      timeoutRetryCountRef.current = 0
      dispatch(setMultiCartValidated({ value: true }))

      if (hasSameCartErrors) {
        handleAllSellerCarts()
        return
      }

      // read from store.getState() instead of the getSellerData selector
      // because getSellerData is a stale snapshot — errors are written to Redux
      // during validation and won't be reflected in the selector until re-render
      const hasSilentErrors = store
        .getState()
        .cart.cartData.vehicles.some((v) =>
          getVehicleProductsErrors(v).some((p) =>
            p.product.errors?.some(
              (e) => e.errorType !== AvailabilityErrorType.PRICE_OVERRIDE
            )
          )
        )
      if (hasSilentErrors || vehiclePriceChangeErrors?.length)
        setHasValidationFailed(true)

      if (vehiclePriceChangeErrors?.length) {
        showNiceModal(CartPriceChangedProductsModal, {
          title: 'importantMessageAboutItemsInYourCart',
          primaryButtonText: 'confirmOrder',
          secondaryButtonText: 'returnToCart',
          vehiclePriceChangeErrors,
        })
        return
      }

      // optional: auto place order if no errors at all
      // handleAllSellerCarts()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Cart validation failed', error)
    } finally {
      isValidatingCartRef.current = false
    }
  }

  const handleCartOrderConfirmation = (): void => {
    if (vehiclesWithParts.length > 1) {
      showNiceModal(CofirmationModal, {
        title: 'orderAllCartsConfirmation',
        primaryActionText: 'yes',
        secondaryActionText: 'no',
        children: showVehiclesData(getMultiVehiclesInfo()),
      }).then((data) => {
        if (data) {
          // multi-vehicle
          validateCartAvailabilityErrors()
        }
      })
    } else {
      // single vehicle
      validateCartAvailabilityErrors()
    }
  }

  return (
    <ButtonGroup>
      {isOrderButtonDisplayed && !cxmlData && (
        <>
          {/* shown after validation finds silent errors (e.g. cant-be-fulfilled parts) */}
          {hasValidationFailed && (
            <OrderExceptionsMessage>
              <OrderExceptionsTitle>
                <span
                  className="material-icons md-dark text-warning"
                  style={{ marginRight: '8px', fontSize: '18px' }}
                >
                  warning
                </span>
                <I18n name="orderExceptionsMessage" />
              </OrderExceptionsTitle>
              <OrderExceptionsSubText>
                <I18n name="orderExceptionsSubMessage" />
              </OrderExceptionsSubText>
            </OrderExceptionsMessage>
          )}
          <OrderButton
            disabled={vehiclesWithParts.length <= 0 || processingOrder}
            id="order-button"
            text={hasValidationFailed ? 'proceedWithOrder' : orderButtonText}
            onClick={() => {
              handleCartOrderConfirmation()
            }}
            variant="primary"
            loading={processingOrder}
          />
        </>
      )}
      {IS_INTEGRATED_VERSION && (
        <>
          {isCartNormalMode && isTransferButtonDisplayed && (
            <TransferButton
              id="transfer-button"
              text="transfer"
              onClick={handleTransfer}
              variant="secondary"
            />
          )}
          <CancelButton
            text="cancel"
            onClick={cancelTransfer}
            variant="danger"
          />
        </>
      )}
      {cxmlData && (
        <TransferButton
          text="transfer"
          onClick={() => {
            setProcessingTransfer(true)
            transferAllCarts(token)
              .then((transferPartsData) => {
                store.dispatch(
                  cartSlice.actions.removeAllVehiclesFromCart(
                    GaTrackOption.doNotTrack
                  )
                )
                StoreInstances.userStore.signOut(false)
                submitTransferPartsForm(
                  transferPartsData?.data || '',
                  transferPartsData?.returnUrl || ''
                )
              })
              .finally(() => {
                setProcessingTransfer(false)
              })
          }}
          variant="primary"
          loading={processingTransfer}
        />
      )}
    </ButtonGroup>
  )
})

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-grow: 1;
  margin-top: 28px;
  @media (max-width: ${breakpoints.laptop}px) {
    flex-direction: row;
  }
`
const OrderButton = styled(Button)`
  @media (max-width: ${breakpoints.laptop}px) {
    width: 100%;
    order: 2;
  }
`

const VehicleContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  align-items: center;
`
const MainContainer = styled.div`
  margin-top: 15px;
  margin-bottom: 15px;
`

const Label = styled.div`
  font-weight: bold;
  font-family: Roboto;
  font-size: 14px;
  margin: 4px;
  text-align: left;
`

const TransferButton = styled(Button)`
  @media (max-width: ${breakpoints.laptop}px) {
    width: 100%;
    order: 1;
  }
`
const CancelButton = styled(Button)`
  @media (max-width: ${breakpoints.laptop}px) {
    width: 100%;
    order: 0;
  }
`

const OrderExceptionsMessage = styled.div`
  background-color: ${(p) => p.theme.colors.yellowLight};
  padding: 8px 12px;
  font-family: Roboto, serif;
  color: ${(p) => p.theme.colors.primaryText};
  margin-bottom: 8px;
`

const OrderExceptionsTitle = styled.div`
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 12px;
  line-height: 18px;
`

const OrderExceptionsSubText = styled.div`
  font-size: 11px;
  line-height: 16px;
  margin-top: 2px;
  padding-left: 26px;
`
